import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useActionChoices } from "stores";
import { SmallText, Title } from "components";
import { useActionModalGetContext } from "context/ActionModalProvider";
import { FormNumberInput } from "form";
import { ACTION_STATUS } from "constants/actions";
import { ACTION_VIEW_MODE } from "constants/action-form";
import { OPERATIONS } from "constants/actions";
import DateSpan from "../date-span/DateSpan";
import DetailSection from "../DetailSection";
import AnalysisResults from "../analysis-results/AnalysisResults";
import ActionModalDescriptionSection from "../description/ActionModalDescriptionSection";
import { useAnalysisFields } from "./useAnalysisFields";

const DISABLED_BULK_OPERATIONS = [
  OPERATIONS.DIAGNOSE.SOIL_SAMPLING,
  OPERATIONS.DIAGNOSE.LEAF_ANALYSIS,
  OPERATIONS.DIAGNOSE.INSECT_MONITORING,
];
const DiagnosePerformed = ({ viewMode, fieldInfo, isNewVersion }) => {
  const { t } = useTranslation();
  const { isSpecificOperation, getOperationById } = useActionChoices();
  const { isBulk } = useActionModalGetContext();
  const { values, setFieldValue } = useFormikContext();

  const status = ACTION_STATUS.performed;
  const isViewMode = viewMode === ACTION_VIEW_MODE.view;

  const { analysisFields } = useAnalysisFields(viewMode);

  const isInsectWatchingOperation = useMemo(() => {
    return isSpecificOperation?.(
      OPERATIONS.DIAGNOSE.INSECT_MONITORING,
      values?.diagnoseAction?.operationTypeId,
    );
  }, [isSpecificOperation, values?.diagnoseAction?.operationTypeId]);

  const checkIfDisabledOperation = useCallback(
    (operationTypeId) => {
      return (
        isBulk &&
        DISABLED_BULK_OPERATIONS.includes(getOperationById(operationTypeId))
      );
    },
    [getOperationById, isBulk],
  );

  useEffect(() => {
    const isDisabledOperationChosen = checkIfDisabledOperation(
      values?.diagnoseAction?.operationTypeId,
    );
    if (isDisabledOperationChosen) {
      setFieldValue("diagnoseAction.operationTypeId", "");
    }
  }, [
    values?.diagnoseAction?.operationTypeId,
    checkIfDisabledOperation,
    setFieldValue,
  ]);

  return (
    <div className="action-modal-card action-details-box">
      <div className="details-half-width">
        <Title title={t("ActionModal.Details.Performed")} />
        {!isNewVersion && <DateSpan viewMode={viewMode} status={status} />}
      </div>
      {isInsectWatchingOperation && (
        <DetailSection
          title={t("ActionModal.Insects.Title")}
          isRequired={!isViewMode}
          isNewVersion={isNewVersion}
        >
          <FormNumberInput
            sx={{ width: "25%" }}
            name="diagnoseAction.quantity"
            slotProps={{
              input: { endAdornment: t("ActionModal.Insects.Units") },
            }}
          />
          <SmallText className="field-description">
            {t("ActionModal.Insects.Description")}
          </SmallText>
        </DetailSection>
      )}
      {analysisFields ? (
        <DetailSection title={t("ActionModal.AnalysisResult.Title")} isNewVersion={isNewVersion}>
          <AnalysisResults
            field={fieldInfo}
            analysisFields={analysisFields}
            isViewMode={isViewMode}
          />
        </DetailSection>
      ) : (
        <div className="details-half-width">
          <ActionModalDescriptionSection
            task="diagnose"
            isViewMode={isViewMode}
            isNewVersion={isNewVersion}
          />
        </div>
      )}
    </div>
  );
};

export default DiagnosePerformed;
